/**
 * **Disposable fixture — Log Sources.**
 *
 * Authored for the same reason as `collectors.ts`: the Log Source lifecycle has
 * no backend yet. Twelve rows across the four fixture Collectors, matching the
 * per-Collector counts the design's list frame prints (4 · 3 · 2 · 3).
 *
 * Vendor/type/port combinations are the design's own (`firewall · udp/514`,
 * `ngfw · tcp/1514`, `nxlog · tcp/601`, `sd-wan · udp/5140`). Ports are
 * unique per Collector — a port conflict on one Collector is a state the
 * Add Log Source flow blocks, so no fixture may present one as normal.
 *
 * Ids are **UUIDs** for the reason `collectors.ts` gives — they reach the URL,
 * and the breadcrumb only hides a segment that parses as one.
 *
 * **Delete condition:** the first real Log Sources endpoint. Deleted together
 * with `collectors.ts`.
 *
 * **Not demo-world content** (ADR 0017) — see `collectors.ts`.
 */
import type { LogSource } from '../types'

export type LogSourceFixture = Omit<LogSource, 'lastLogAt'> & {
	/**
	 * Seconds since the last log arrived, resolved to `lastLogAt` on fetch — the
	 * same offset-not-timestamp trick `collectors.ts` uses, and for the same
	 * reason. `null` = nothing has ever arrived (`awaiting_first_logs`).
	 */
	lastLogSecondsAgo: number | null
}

export const LOG_SOURCE_FIXTURES: LogSourceFixture[] = [
	// HQ — Detroit, MI · fully healthy, and the row the design shows expanded.
	{
		id: '9e3a5b10-77c2-4f8d-b6a1-1c4e2f900101',
		collectorId: '6b1f0c2a-3d4e-4a71-9c58-0f2b7d51a801',
		name: 'Cisco ASA — HQ — Detroit, MI',
		type: 'firewall',
		transport: 'udp',
		port: 514,
		status: 'receiving',
		lastLogSecondsAgo: 4,
	},
	{
		id: '9e3a5b10-77c2-4f8d-b6a1-1c4e2f900102',
		collectorId: '6b1f0c2a-3d4e-4a71-9c58-0f2b7d51a801',
		name: 'Palo Alto — HQ — Detroit, MI',
		type: 'ngfw',
		transport: 'tcp',
		port: 1514,
		status: 'receiving',
		lastLogSecondsAgo: 2,
	},
	{
		id: '9e3a5b10-77c2-4f8d-b6a1-1c4e2f900103',
		collectorId: '6b1f0c2a-3d4e-4a71-9c58-0f2b7d51a801',
		name: 'Windows Domain Controller — HQ — Detroit, MI',
		type: 'nxlog',
		transport: 'tcp',
		port: 601,
		status: 'receiving',
		lastLogSecondsAgo: 31,
	},
	{
		id: '9e3a5b10-77c2-4f8d-b6a1-1c4e2f900104',
		collectorId: '6b1f0c2a-3d4e-4a71-9c58-0f2b7d51a801',
		name: 'Meraki — HQ — Detroit, MI',
		type: 'sd-wan',
		transport: 'udp',
		port: 5140,
		status: 'receiving',
		lastLogSecondsAgo: 12,
	},

	// Branch OKC, OK · one silent source under a connected agent — the pair of
	// facts the two status enums exist to keep apart.
	{
		id: '9e3a5b10-77c2-4f8d-b6a1-1c4e2f900201',
		collectorId: '6b1f0c2a-3d4e-4a71-9c58-0f2b7d51a802',
		name: 'Fortinet FortiGate — Branch OKC, OK',
		type: 'firewall',
		transport: 'udp',
		port: 514,
		status: 'receiving',
		lastLogSecondsAgo: 7,
	},
	{
		id: '9e3a5b10-77c2-4f8d-b6a1-1c4e2f900202',
		collectorId: '6b1f0c2a-3d4e-4a71-9c58-0f2b7d51a802',
		name: 'Windows Domain Controller — Branch OKC, OK',
		type: 'nxlog',
		transport: 'tcp',
		port: 601,
		status: 'receiving',
		lastLogSecondsAgo: 45,
	},
	{
		id: '9e3a5b10-77c2-4f8d-b6a1-1c4e2f900203',
		collectorId: '6b1f0c2a-3d4e-4a71-9c58-0f2b7d51a802',
		name: 'Ubiquiti — Branch OKC, OK',
		type: 'network',
		transport: 'udp',
		port: 5140,
		status: 'silent',
		lastLogSecondsAgo: 93600,
	},

	// Branch — Austin, TX · degraded agent.
	{
		id: '9e3a5b10-77c2-4f8d-b6a1-1c4e2f900301',
		collectorId: '6b1f0c2a-3d4e-4a71-9c58-0f2b7d51a803',
		name: 'Cisco ASA — Branch — Austin, TX',
		type: 'firewall',
		transport: 'udp',
		port: 514,
		status: 'receiving',
		lastLogSecondsAgo: 19,
	},
	{
		id: '9e3a5b10-77c2-4f8d-b6a1-1c4e2f900302',
		collectorId: '6b1f0c2a-3d4e-4a71-9c58-0f2b7d51a803',
		name: 'Palo Alto — Branch — Austin, TX',
		type: 'ngfw',
		transport: 'tcp',
		port: 1514,
		status: 'silent',
		lastLogSecondsAgo: 100800,
	},

	// St. Johns, MI · offline agent, so nothing on it can be receiving.
	{
		id: '9e3a5b10-77c2-4f8d-b6a1-1c4e2f900401',
		collectorId: '6b1f0c2a-3d4e-4a71-9c58-0f2b7d51a804',
		name: 'Cisco ASA — St. Johns, MI',
		type: 'firewall',
		transport: 'udp',
		port: 514,
		status: 'silent',
		lastLogSecondsAgo: 93600,
	},
	{
		id: '9e3a5b10-77c2-4f8d-b6a1-1c4e2f900402',
		collectorId: '6b1f0c2a-3d4e-4a71-9c58-0f2b7d51a804',
		name: 'Palo Alto — St. Johns, MI',
		type: 'ngfw',
		transport: 'tcp',
		port: 1514,
		status: 'silent',
		lastLogSecondsAgo: 172800,
	},
	{
		id: '9e3a5b10-77c2-4f8d-b6a1-1c4e2f900403',
		collectorId: '6b1f0c2a-3d4e-4a71-9c58-0f2b7d51a804',
		name: 'Ubiquiti — St. Johns, MI',
		type: 'network',
		transport: 'udp',
		port: 5140,
		status: 'awaiting_first_logs',
		lastLogSecondsAgo: null,
	},
]
